// Magic Engine v1 — core registry, settings, and triggerMagicMoment hook.
//
// Architecture
// ------------
// Settings live in localStorage (jingle.magic.settings.v1) and are read
// reactively via a module-level broadcast channel so all hook instances
// stay in sync without requiring a top-level Context.
//
// emitMagicMoment() can be called from anywhere (page, component, hook).
// It reads the current settings, plays sound if enabled, and dispatches
// a custom DOM event that <MagicEngineOverlay> listens to for rendering
// animations. This keeps the engine additive — nothing in the base flows
// is gated on the engine being mounted.
//
// Out-of-scope for v1 (see IMAGE_PROCESSING_PIPELINE_TODO.md for the
// related asset pipeline):
//   - Real SFX audio files (v1 uses Web Audio synthesis only)
//   - Lottie animations, Framer Motion sequences
//   - Background music, video loops, haptics

import { useEffect, useState } from "react";
// CharacterMood is imported as a type so MagicMomentContext.mood is bound to
// the canonical mood union (rather than a loose string). This rejects typos
// at the call site and removes the need for any runtime cast in the overlay.
import type { CharacterMood } from "./magic-characters";

// ── Settings ────────────────────────────────────────────────────────────────

export type MagicMotion = "full" | "gentle" | "minimal";

export interface MagicSettings {
  soundEnabled: boolean;
  voiceEnabled: boolean;
  magicMotion: MagicMotion;
  ambientEnabled: boolean;
  respectReducedMotion: boolean;
  /**
   * Slugs of first-time-only magic moments this device has already
   * seen. Persisted across sessions so families don't get the same
   * one-shot beats (e.g. first-clue-opened, mission-control-milestone)
   * re-triggering on every visit. Recurring moments — voice playback,
   * photo submitted, map lit up, etc. — are unaffected and continue
   * to fire every time, since those are per-action feedback rather
   * than narrative milestones.
   */
  seenMoments: MagicMomentId[];
}

const SETTINGS_KEY = "jingle.magic.settings.v1";
const SETTINGS_CHANGE_EVENT = "jingle:magic:settings-change";

const DEFAULT_SETTINGS: MagicSettings = {
  soundEnabled: true,
  voiceEnabled: true,
  magicMotion: "full",
  ambientEnabled: true,
  respectReducedMotion: true,
  seenMoments: [],
};

function loadSettings(): MagicSettings {
  try {
    if (typeof window === "undefined") return DEFAULT_SETTINGS;
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<MagicSettings>;
    const merged = { ...DEFAULT_SETTINGS, ...parsed };
    // Defensive: ensure seenMoments is always an array of strings, even if
    // a previous build wrote bad data (Set serialized as {}, undefined,
    // an object, etc.) — guards against `.includes()` blowing up below.
    if (!Array.isArray(merged.seenMoments)) {
      merged.seenMoments = [];
    } else {
      merged.seenMoments = merged.seenMoments.filter(
        (s): s is MagicMomentId => typeof s === "string",
      );
    }
    return merged;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function saveSettings(s: MagicSettings): void {
  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
    window.dispatchEvent(new Event(SETTINGS_CHANGE_EVENT));
  } catch {}
}

/** Read the current magic settings once (synchronous, no React). */
export function readMagicSettings(): MagicSettings {
  return loadSettings();
}

/** Persist a partial settings update. */
export function patchMagicSettings(patch: Partial<MagicSettings>): void {
  const next = { ...loadSettings(), ...patch };
  saveSettings(next);
}

/** React hook — returns [settings, patcher]. Re-renders when settings change. */
export function useMagicSettings(): [MagicSettings, (patch: Partial<MagicSettings>) => void] {
  const [settings, setSettings] = useState<MagicSettings>(loadSettings);

  useEffect(() => {
    const handler = () => setSettings(loadSettings());
    window.addEventListener(SETTINGS_CHANGE_EVENT, handler);
    return () => window.removeEventListener(SETTINGS_CHANGE_EVENT, handler);
  }, []);

  function patch(p: Partial<MagicSettings>) {
    const next = { ...settings, ...p };
    saveSettings(next);
    setSettings(next);
  }

  return [settings, patch];
}

// ── Moment registry ─────────────────────────────────────────────────────────

export type MagicMomentId =
  | "santa-letter-opens"
  | "voice-playback-starts"
  | "first-clue-opened"
  | "photo-submitted"
  | "offline-photo-saved"
  | "queued-sync-succeeded"
  | "map-lights-up"
  | "inbox-message-revealed"
  | "recap-published-opens"
  | "mission-control-milestone"
  /**
   * Fires once per calendar day when a player opens the HQ/home surface.
   * Used as the "hub wakes up" ambient acknowledgement — a gentle gold
   * glow pulse + banner that says the workshop is ready. The daily gate
   * is enforced at the call site (home.tsx) via localStorage, so this
   * moment itself is not registered as first-time-only (recurring daily).
   */
  | "hub-daily-opens"
  /**
   * Mid-mission Coalie heist beat — fires once per mission when the team
   * crosses 50% of approved clues. Full-screen takeover where Coalie
   * (the sneaky cartoon-thief soot-puff) tries to barge onto the screen
   * to swipe Christmas Spirit; the elves shove him back. Playful, never
   * scary, never sad.
   */
  | "coalie-takeover-mid"
  /**
   * Final celebration when the team reaches 100% of approved clues.
   * Full-screen fireworks cinematic — the family has pushed Coalie back
   * for good and the Spirit Map is blazing.
   * Registered as first-time-only — fires exactly once per mission.
   */
  | "mission-complete-celebration"
  /**
   * Postcard send-off animation — a brief envelope-flying-into-snow beat
   * shown when a player successfully sends a postcard. Recurring (every
   * postcard sent gets its own moment).
   */
  | "postcard-sent"
  /**
   * Tier-1 cinematic takeover when a family first opens their published
   * Keepsake Recap. Replaces the old `recap-published-opens` flourish
   * with a screen-filling confetti shower, AuroraPulse, Santa portrait
   * with halo, and a spoken Santa congratulations line. Registered as
   * first-time-only — the season's biggest emotional milestone should
   * land exactly once per device.
   */
  | "recap-season-complete"
  /**
   * Verified-find letter celebration — fires the first time a family
   * opens a "praise" inbox letter (Claude Vision confirmed the find).
   * Pure decorative gold burst anchored on the wax seal so the
   * celebratory beat from the HQ Open-Next-Clue tile carries through
   * into the narrative letter. Gated per-letter via the mission-scoped
   * sessionStorage gate so stale rereads never replay it.
   */
  | "verified-find-letter-burst";

export interface MagicMomentContext {
  /** Element to anchor animation near (bounding box used). */
  anchor?: HTMLElement | null;
  /** Optional extra data the animation may use. */
  data?: Record<string, unknown>;
  /**
   * Optional per-instance mood override for the character portrait. When
   * present, the MagicEngineOverlay's CharacterPortrait selects the matching
   * artwork variant (e.g. "celebrating" vs "neutral"). When absent, the
   * default mood from MOMENT_CHARACTER_MAP is used. Pure presentation hint —
   * never affects sound, animation primitives, or moment lifecycle.
   */
  mood?: CharacterMood;
}

export interface MagicMomentEvent {
  momentId: MagicMomentId;
  ctx?: MagicMomentContext;
  instanceId: string;
  firedAt: number;
  /** Whether the current OS reduced-motion preference was active when fired. */
  osReducedMotion: boolean;
  /** Resolved magic motion level at the time of firing. */
  resolvedMotion: MagicMotion | "none";
  /** Whether sound was enabled at the time of firing. */
  soundEnabled: boolean;
}

const MAGIC_MOMENT_EVENT = "jingle:magic:moment";

// Registry: moment → SFX slug (maps to magic-sound.ts).
// See MAGIC_ENGINE_V1_PLAN.md for the canonical sound/moment pairing.
const MOMENT_SOUND_MAP: Partial<Record<MagicMomentId, string>> = {
  "santa-letter-opens": "gentle-chime",
  "voice-playback-starts": "gentle-ping",
  "first-clue-opened": "sparkle-burst",
  "photo-submitted": "fanfare",
  "offline-photo-saved": "wax-seal",
  "queued-sync-succeeded": "success-ping",
  "map-lights-up": "aurora-whoosh",
  "inbox-message-revealed": "gentle-chime",
  "recap-published-opens": "fanfare",
  "mission-control-milestone": "fanfare",
  "hub-daily-opens": "gentle-chime",
  "coalie-takeover-mid": "fanfare",
  "mission-complete-celebration": "fanfare",
  "postcard-sent": "gentle-chime",
  "recap-season-complete": "fanfare",
};

// Animation level per moment (the minimum motion level needed to fire)
const MOMENT_MOTION_THRESHOLD: Record<MagicMomentId, MagicMotion | "always"> = {
  "santa-letter-opens": "gentle",
  "voice-playback-starts": "gentle",
  "first-clue-opened": "gentle",
  "photo-submitted": "full",
  "offline-photo-saved": "minimal",
  "queued-sync-succeeded": "gentle",
  "map-lights-up": "full",
  "inbox-message-revealed": "gentle",
  // "full" threshold fires for ALL motion levels in this engine (motionAllows
  // uses order=["full","gentle","minimal"] and indexOf(current)>=indexOf(required),
  // so a required index of 0 is always satisfied). This ensures every family
  // — full, gentle, and minimal — reaches this moment with their own
  // effectiveMotion value, so the overlay can render the correct tier for them.
  "recap-published-opens": "full",
  "mission-control-milestone": "gentle",
  "hub-daily-opens": "full",
  "coalie-takeover-mid": "full",
  "mission-complete-celebration": "full",
  "postcard-sent": "gentle",
  // "full" threshold = always fires (motionAllows uses index comparison),
  // so every family — full/gentle/minimal — reaches this Tier-1 moment with
  // their own effectiveMotion value. The overlay then renders the right
  // tier (sparkles vs static toast) per the resolved level.
  "recap-season-complete": "full",
  // "full" threshold = always fires (motionAllows uses index comparison),
  // so families on every motion tier reach the burst. The NgSuccessBurst
  // primitive itself short-circuits under OS prefers-reduced-motion.
  "verified-find-letter-burst": "full",
};

// Moments registered here fire ONCE per device — after the first
// dispatch the slug is added to MagicSettings.seenMoments, and
// subsequent emit calls early-return. Use this for narrative milestones
// that should feel special (the first clue ever opened, the moment
// the mission becomes ready to launch, the recap publishing flourish)
// — not for per-action feedback like a photo upload or a voice play.
//
// All other moments stay recurring by default. To make a recurring
// moment one-shot, add it here. To re-experience a one-shot moment
// (e.g. a parent wants to film their second kid's reaction), the
// MagicSettingsPanel exposes a "Reset seen moments" button.
const MOMENT_FIRST_TIME_ONLY: Partial<Record<MagicMomentId, true>> = {
  "first-clue-opened": true,
  "mission-control-milestone": true,
  "recap-published-opens": true,
  // Tier-1 season finale — fires exactly once per device. The recap page
  // call site also adds a mission-scoped sessionStorage gate (hasBeenFired
  // / markFired) for defence-in-depth so a tab refresh between publish and
  // the family opening the recap can't replay the takeover.
  "recap-season-complete": true,
  // NOTE: coalie-takeover-mid and mission-complete-celebration are
  // intentionally NOT registered here. They are gated mission-scoped
  // (not device-scoped) at the call site via hasBeenFired/markFired
  // keyed by missionId, so a family playing a SECOND mission still
  // gets the full Coalie heist beat + the elves shoving him out and
  // the victory celebration. Marking them device-scoped one-shot
  // would suppress the moment for every mission after the first.
};

/** True if `id` is registered as a one-shot per-device moment. */
export function isFirstTimeMoment(id: MagicMomentId): boolean {
  return Boolean(MOMENT_FIRST_TIME_ONLY[id]);
}

/** True if this device has already seen `id` (only meaningful for first-time-only moments). */
export function hasSeenMoment(id: MagicMomentId): boolean {
  return readMagicSettings().seenMoments.includes(id);
}

/** Clear the seen-moments list so first-time-only moments fire again. */
export function resetSeenMoments(): void {
  patchMagicSettings({ seenMoments: [] });
}

// ── Mission-scoped fired-moment store ───────────────────────────────────────
//
// Some narrative moments — first-clue-opened (#3), map-lights-up (#7),
// inbox-message-revealed (#8), recap-published-opens (#9) — should fire
// only ONCE per mission, even across page refreshes. The legacy
// useRef-only gate at each call site lost that state on every page
// reload, so families navigating around saw the same flourish replay
// every visit.
//
// Storage: sessionStorage, key `jingle.magic.fired.v1.<missionId>`,
// value = JSON array of MagicMomentId strings. sessionStorage scope
// (per browser tab session) keeps the gate snug — a brand-new tab or
// a brand-new mission both reset cleanly without polluting localStorage
// or competing with the device-wide `seenMoments` list above.
//
// Defensive: missionId is optional. When undefined (session not loaded
// yet), hasBeenFired returns false and markFired no-ops, so the call
// site simply waits for a real missionId before recording. This avoids
// writing to a `…undefined` key when navigation reaches the page
// faster than the session query.

const FIRED_KEY_PREFIX = "jingle.magic.fired.v1.";

function firedKey(missionId: string | number): string {
  return `${FIRED_KEY_PREFIX}${missionId}`;
}

function readFiredList(missionId: string | number): MagicMomentId[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(firedKey(missionId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is MagicMomentId => typeof s === "string");
  } catch {
    return [];
  }
}

/**
 * True if `momentId` has already fired during this browser session for
 * this mission. Returns false when missionId is undefined or
 * sessionStorage is unavailable (e.g. server render, locked-down
 * private-mode browsers) so the moment is allowed to fire — better to
 * over-fire once than silently swallow a milestone.
 */
export function hasBeenFired(
  momentId: MagicMomentId,
  missionId: string | number | undefined,
): boolean {
  if (missionId === undefined || missionId === null || missionId === "") {
    return false;
  }
  return readFiredList(missionId).includes(momentId);
}

/**
 * Record that `momentId` fired for this mission in this browser
 * session. Idempotent (safe to call when already recorded). No-ops
 * cleanly when missionId is undefined or sessionStorage is unavailable.
 */
export function markFired(
  momentId: MagicMomentId,
  missionId: string | number | undefined,
): void {
  if (typeof window === "undefined") return;
  if (missionId === undefined || missionId === null || missionId === "") {
    return;
  }
  try {
    const list = readFiredList(missionId);
    if (list.includes(momentId)) return;
    list.push(momentId);
    window.sessionStorage.setItem(firedKey(missionId), JSON.stringify(list));
  } catch {
    /* sessionStorage unavailable — soft fail; the worst case is a moment
       that re-fires on the next page load, which is the prior behavior. */
  }
}

function motionAllows(
  required: MagicMotion | "always",
  current: MagicMotion,
): boolean {
  if (required === "always") return true;
  const order: MagicMotion[] = ["full", "gentle", "minimal"];
  return order.indexOf(current) >= order.indexOf(required);
}

function getOsReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
}

let _instanceCounter = 0;
function nextInstanceId(): string {
  return `magic-${Date.now()}-${++_instanceCounter}`;
}

/**
 * Fire a magic moment. Safe to call anywhere — no React required.
 * The engine reads the current settings, plays audio if enabled, and
 * dispatches a custom event that <MagicEngineOverlay> picks up to
 * render animations.
 */
export function emitMagicMoment(
  momentId: MagicMomentId,
  ctx?: MagicMomentContext,
): void {
  if (typeof window === "undefined") return;

  // First-time-only gate: if this moment is registered as one-shot,
  // atomically check-and-record BEFORE doing any dispatch work.
  // Persisting before (rather than after) the dispatch defends against
  // a synchronous re-entrant emit — if a listener triggers
  // emitMagicMoment again in the same tick, the re-entry observes the
  // record and short-circuits, preventing duplicate one-shot playback.
  // Re-reading settings here (rather than relying on a snapshot above)
  // also protects against interleaved writes for *different* slugs:
  // patchMagicSettings does {...loadSettings(), ...patch}, so the
  // union of seenMoments is preserved across nested calls.
  if (MOMENT_FIRST_TIME_ONLY[momentId]) {
    const fresh = readMagicSettings();
    if (fresh.seenMoments.includes(momentId)) {
      return; // already seen — suppress all output
    }
    patchMagicSettings({
      seenMoments: [...fresh.seenMoments, momentId],
    });
  }

  const settings = readMagicSettings();
  const osRM = getOsReducedMotion();
  const reducedByOS = osRM && settings.respectReducedMotion;

  // Resolve the effective motion level (OS override takes effect)
  let resolvedMotion: MagicMotion | "none" = settings.magicMotion;
  if (reducedByOS) resolvedMotion = "none";

  // Play sound if enabled (async, non-blocking).
  // NOTE: OS reduced-motion preference only suppresses *animation*; sound
  // continues unless the user has turned off soundEnabled explicitly.
  const sfxSlug = MOMENT_SOUND_MAP[momentId];
  if (settings.soundEnabled && sfxSlug && sfxSlug !== "none") {
    import("./magic-sound").then(({ playMagicSound }) => {
      playMagicSound(sfxSlug as Parameters<typeof playMagicSound>[0]).catch(
        () => {},
      );
    });
    // Haptic pulse: 30 ms — short enough to feel like a confirmation tap,
    // not an alert. Gracefully absent on iOS (no Vibration API) and when
    // the device is in silent mode (vibrate() is a no-op there too).
    try {
      navigator.vibrate?.(30);
    } catch {
      // Ignore — vibrate() may throw in restricted browser contexts.
    }
  }

  // Determine if animation should fire
  const threshold = MOMENT_MOTION_THRESHOLD[momentId];
  const shouldAnimate =
    resolvedMotion !== "none" &&
    motionAllows(threshold, resolvedMotion as MagicMotion);

  const payload: MagicMomentEvent = {
    momentId,
    ctx,
    instanceId: nextInstanceId(),
    firedAt: Date.now(),
    osReducedMotion: osRM,
    resolvedMotion: shouldAnimate ? resolvedMotion : "none",
    soundEnabled: settings.soundEnabled,
  };

  window.dispatchEvent(
    new CustomEvent<MagicMomentEvent>(MAGIC_MOMENT_EVENT, { detail: payload }),
  );
}

/** Hook — returns a stable trigger function. */
export function useTriggerMagicMoment(): (
  id: MagicMomentId,
  ctx?: MagicMomentContext,
) => void {
  return emitMagicMoment;
}

/** Hook — subscribes to magic moment events (used by <MagicEngineOverlay>). */
export function useMagicMomentEvents(
  handler: (evt: MagicMomentEvent) => void,
): void {
  useEffect(() => {
    const listener = (e: Event) => {
      handler((e as CustomEvent<MagicMomentEvent>).detail);
    };
    window.addEventListener(MAGIC_MOMENT_EVENT, listener);
    return () => window.removeEventListener(MAGIC_MOMENT_EVENT, listener);
  }, [handler]);
}

export { MAGIC_MOMENT_EVENT };
